import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, API } from "./api";
import axios from "axios";

// ---------------------------------------------------------------------------
// Phase C auth: httpOnly cookie transport.
// ---------------------------------------------------------------------------
// Tokens no longer live in localStorage / sessionStorage. The server sets a
// `ych_auth` httpOnly Secure cookie on /auth/mobile/login and
// /auth/mobile/register, and every request from `api` (axios) sends it via
// `withCredentials: true`. This closes the XSS token-theft window that the
// Emergent K8s ingress opened by rewriting CORS to `*`.
//
// One-time migration nicety: on first load after this ship we clean out any
// stale legacy token keys so old localStorage values don't linger forever.
// We DON'T try to auto-re-use them — a one-time re-login is the deliberate
// cutover point (users see a banner explaining the security upgrade on the
// LoginScreen).
const LEGACY_TOKEN_KEY = "ych_auth_token";
const LEGACY_PERSIST_KEY = "ych_auth_persist";
const CUTOVER_BANNER_KEY = "ych_auth_cutover_banner_seen";
// Key that stores the local-date string (YYYY-MM-DD) of the last time we
// force-reloaded this browser after an authenticated boot. Used to pull
// the newest frontend bundle exactly once per day for returning users
// (see `maybeDailyRefresh` below).
const DAILY_REFRESH_KEY = "ych_daily_refresh_ymd";

function purgeLegacyTokens() {
  try {
    // Only run the notice logic if we actually found a legacy token —
    // otherwise brand-new users would see the "we upgraded security"
    // banner too, which is confusing.
    const hadLegacy =
      !!window.localStorage.getItem(LEGACY_TOKEN_KEY) ||
      !!window.sessionStorage.getItem(LEGACY_TOKEN_KEY);
    window.localStorage.removeItem(LEGACY_TOKEN_KEY);
    window.localStorage.removeItem(LEGACY_PERSIST_KEY);
    window.sessionStorage.removeItem(LEGACY_TOKEN_KEY);
    if (hadLegacy && !window.localStorage.getItem(CUTOVER_BANNER_KEY)) {
      window.localStorage.setItem(CUTOVER_BANNER_KEY, "pending");
    }
  } catch {
    /* private-mode / storage disabled — noop */
  }
}

/**
 * @returns "pending" once (immediately after upgrade for users who had a
 *   legacy localStorage token), otherwise `null`. Once the banner is
 *   dismissed we flip the flag so it never re-appears.
 */
export function cutoverBannerState() {
  try { return window.localStorage.getItem(CUTOVER_BANNER_KEY); }
  catch { return null; }
}

export function dismissCutoverBanner() {
  try { window.localStorage.setItem(CUTOVER_BANNER_KEY, "dismissed"); }
  catch { /* noop */ }
}

/**
 * Force a hard reload of the app AT MOST once per calendar day, but only
 * for a user who already has an authenticated cookie (so the reload just
 * pulls a newer bundle — the login screen isn't revisited).
 *
 * Coach flow this covers: prod is redeployed every day. When a returning
 * user opens the app on day N with a still-valid JWT cookie, the last
 * force-reload happened on day (N-1); we bump the marker to today and
 * `location.reload()` so they immediately have the newly-deployed
 * frontend. The `Gmail-style useAutoReload` hook already handles quiet
 * mid-session upgrades — this daily one is a belt-and-braces guarantee.
 *
 * Notes:
 *   • We DO NOT trigger on a fresh mobile login / register — those users
 *     just downloaded the latest bundle from the login screen, so a
 *     reload would be pointless (and jarring).
 *   • Local calendar date is used (not UTC) so the switchover matches
 *     the coach's day. Timezone changes during a session are ignored;
 *     they'll self-correct on the next session boot.
 *   • First-ever visit: no key present → we seed today's date and skip
 *     the reload (no stale bundle to swap out yet).
 */
function todayYmd() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function maybeDailyRefresh() {
  try {
    const today = todayYmd();
    const prev  = window.localStorage.getItem(DAILY_REFRESH_KEY);
    if (prev === today) return; // already refreshed today
    // Seed today's date BEFORE reloading — otherwise the post-reload
    // boot would read a stale (null / yesterday) value and loop.
    window.localStorage.setItem(DAILY_REFRESH_KEY, today);
    if (prev == null) return; // first-ever visit — no reload needed
    console.info("[auth] daily refresh: pulling latest deployed bundle");
    window.location.reload();
  } catch {
    /* private-mode / storage disabled — silently skip */
  }
}

/**
 * Legacy helper kept for the XMLHttpRequest lab-import upload path —
 * modern browsers use `xhr.withCredentials = true` and the cookie is
 * automatically attached, so this always returns an empty string post-P0.
 * Left in the API surface so no consumer needs to change simultaneously.
 */
export function getAuthToken() {
  return "";
}

const AuthContext = createContext(null);

// Permissions
export const PERMS = ["view", "add", "edit", "delete", "print"];

export function userHasPerm(user, perm) {
  if (!user) return false;
  if (user.role === "admin") return true;
  return Array.isArray(user.permissions) && user.permissions.includes(perm);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  // status: "checking" | "anon" | "authed"
  const [status, setStatus] = useState("checking");
  // Auth feature-flag from the backend. When false the login screen is
  // bypassed and everyone is treated as a virtual admin.
  const [authEnabled, setAuthEnabled] = useState(null); // null = unknown yet

  // One-time cleanup of stale localStorage tokens from the pre-cookie era.
  useEffect(() => { purgeLegacyTokens(); }, []);

  // Logout locally if the server says the cookie is bad/expired.
  useEffect(() => {
    const id = api.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err?.response?.status === 401) {
          setUser(null);
          setStatus("anon");
        }
        return Promise.reject(err);
      },
    );
    return () => api.interceptors.response.eject(id);
  }, []);

  // On mount, probe the server: is auth even on? If yes, is the cookie
  // still valid? The cookie is sent automatically by axios/withCredentials.
  useEffect(() => {
    let cancelled = false;
    const verify = async () => {
      let cfg = { auth_enabled: true };
      try {
        const c = await axios.get(`${API}/auth/config`, { withCredentials: true });
        cfg = c.data || cfg;
      } catch (err) {
        console.warn("[auth] /auth/config probe failed; assuming auth_enabled=true", err?.message);
      }
      if (cancelled) return;
      setAuthEnabled(cfg.auth_enabled);

      if (!cfg.auth_enabled) {
        // Auth disabled at the server — fetch the synthetic admin and unlock the UI.
        try {
          const r = await axios.get(`${API}/auth/me`, { withCredentials: true });
          if (cancelled) return;
          setUser(r.data);
          setStatus("authed");
        } catch (err) {
          console.warn("[auth] /auth/me failed while auth_disabled mode:", err?.message);
          if (cancelled) return;
          setStatus("anon");
        }
        return;
      }

      try {
        const r = await api.get("/auth/me");
        if (cancelled) return;
        setUser(r.data);
        setStatus("authed");
        // Returning-user boot: pull the newest frontend bundle at most
        // once per calendar day. Fresh mobile-login / register paths do
        // NOT call this — they just downloaded the current bundle to
        // render the login screen.
        maybeDailyRefresh();
      } catch (err) {
        // 401 means no valid cookie — user needs to sign in. Anything
        // else is likely a transient network / backend blip; still land
        // on the login screen but log so we can spot real outages.
        if (err?.response?.status !== 401) {
          console.warn("[auth] /auth/me failed:", err?.message);
        }
        if (cancelled) return;
        setUser(null);
        setStatus("anon");
      }
    };
    verify();
    return () => { cancelled = true; };
  }, []);

  /**
   * Mobile + PIN login. Pre-flight checks if the mobile is registered, then
   * either logs in with the PIN or sets a fresh PIN on a pre-authorised
   * (admin-added) account. Returns the user object; the JWT itself lives
   * only in the httpOnly cookie the backend sets on this response.
   */
  const mobileCheck = useCallback(async (mobile) => {
    const r = await axios.post(`${API}/auth/mobile/check`, { mobile }, { withCredentials: true });
    return r.data;
  }, []);

  const mobileLogin = useCallback(async (mobile, pin, remember) => {
    const r = await axios.post(
      `${API}/auth/mobile/login`,
      { mobile, pin, remember: !!remember },
      { withCredentials: true },
    );
    setUser(r.data.user);
    setStatus("authed");
    return r.data.user;
  }, []);

  const mobileRegister = useCallback(async (mobile, pin, name, remember) => {
    const r = await axios.post(
      `${API}/auth/mobile/register`,
      { mobile, pin, name, remember: !!remember },
      { withCredentials: true },
    );
    setUser(r.data.user);
    setStatus("authed");
    return r.data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // If the server call fails (offline / already-expired cookie), fall
      // through — clearing local state is what matters for the UI.
      console.warn("[auth] /auth/logout failed:", err?.message);
    }
    setUser(null);
    setStatus("anon");
  }, []);

  const hasPerm = useCallback((perm) => userHasPerm(user, perm), [user]);

  const refreshUser = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
      return r.data;
    } catch (err) {
      console.warn("[auth] refreshUser failed:", err?.message);
      return null;
    }
  }, []);

  const value = {
    user,
    status,
    isAuthenticated: status === "authed",
    isAdmin: user?.role === "admin",
    authEnabled,
    mobileCheck,
    mobileLogin,
    mobileRegister,
    logout,
    hasPerm,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
